import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from './api'
import type { AgentListItem, AgentListResponse } from './types'

/** Lowercases and hyphenates a display name into a URL-safe slug segment. */
export function toAgentSlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

export interface ResolvedAgent {
  agentId: string
  isResolving: boolean
}

/**
 * Resolves the `:slug` segment of `/agents/:slug/:tab` to an agent id.
 *
 * Reactive (MS-740 review: a cold load / bookmark / plain reload has an empty
 * TanStack Query cache, so a non-reactive cache read treats the slug as a raw
 * id and 404s). Numeric segments resolve immediately with no network call, so
 * every existing `/agents/{id}` deep link and test keeps working unchanged.
 *
 * Frontend-only and best-effort (MS-740 "Slug caveat"): the non-Mambu-built
 * agent list is cursor-paginated (see Agents.tsx), so this only covers
 * agents whose list page loads within the first 100; there is also no DB
 * unique index on `display_name`, so slugs are not collision-proof (first
 * match wins). A custom agent renamed after this segment was generated also
 * won't resolve — the slug doesn't follow a rename.
 * These limits only become reachable once tenant-created agents exist; a
 * follow-up ticket should be raised at that point.
 * When no match is found, the segment is treated as a raw agent id instead —
 * this keeps old `/agents/{id}` deep links working.
 */
export function useResolveAgentId(slugOrId: string): ResolvedAgent {
  const apiFetch = useApiFetch()
  const isNumeric = /^\d+$/.test(slugOrId)
  const isEmpty = slugOrId === ''

  // Reuses the exact key/queryFn Agents.tsx uses for the Mambu-built list, so
  // in-app navigation from the overview hits a warm cache and issues no
  // extra request.
  const mambuQuery = useQuery({
    queryKey: ['agents', 'mambu'],
    queryFn: () => apiFetch<AgentListResponse>('/api/v1/agents?mambuBuilt=true&limit=100'),
    enabled: !isEmpty && !isNumeric,
  })

  const mambuMatch = (mambuQuery.data?.items ?? []).find(
    (item) => toAgentSlug(item.displayName) === slugOrId
  )

  // Only queried on a miss against the Mambu-built list, once it has settled.
  // Distinct key from ['agents', 'custom'] — that one is an infinite query
  // with an incompatible cache shape.
  const customLookupEnabled = !isEmpty && !isNumeric && mambuQuery.isFetched && !mambuMatch
  const customQuery = useQuery({
    queryKey: ['agents', 'custom-slug-lookup'],
    queryFn: () => apiFetch<AgentListResponse>('/api/v1/agents?mambuBuilt=false&limit=100'),
    enabled: customLookupEnabled,
  })

  const customMatch = (customQuery.data?.items ?? []).find(
    (item: AgentListItem) => toAgentSlug(item.displayName) === slugOrId
  )

  if (isEmpty || isNumeric) {
    return { agentId: slugOrId, isResolving: false }
  }

  if (mambuMatch) {
    return { agentId: mambuMatch.id, isResolving: false }
  }

  if (!mambuQuery.isFetched) {
    return { agentId: slugOrId, isResolving: true }
  }

  if (customMatch) {
    return { agentId: customMatch.id, isResolving: false }
  }

  if (customLookupEnabled && !customQuery.isFetched) {
    return { agentId: slugOrId, isResolving: true }
  }

  return { agentId: slugOrId, isResolving: false }
}
